import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowRight, Lock, Check, Loader2 } from 'lucide-react';
import { GLASS } from './glass';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact horizontal-row pricing selector, inspired by hairtamin.com.
 * One active row (pre-order €35) + three greyed subscription rows that
 * unlock at launch. Every row shows a product thumbnail on the left,
 * plan detail in the middle, price on the right, and a select/lock CTA.
 *
 * vs the €45 one-time regular price:
 *   sub1 €42/mo → save ~7 %
 *   sub3 €39/mo → save ~13 %
 *   sub6 €35/mo → save ~22 %   ← "Most popular" pin
 *   preorder €35 → save €10 vs the €45 launch price
 */

const COPY = {
    en: {
        eyebrow: 'Pre-order',
        heading: 'Pre-order Sleepstack.',
        lead: 'A one-off pre-order for €35, no subscription needed. The three subscription plans below open at launch in November/December 2026.',
        bullets: ['One-off pre-order', 'No subscription needed', 'Ships after quality sign-off', 'Full refund until we ship'],
        available_now: 'Available now',
        at_launch: 'Opens at launch · November/December 2026',
        preorder_cta: 'Continue to checkout',
        locked_cta: 'Available at launch',
        fineprint: 'You\'re charged €35 today. We ship in November/December 2026, and only once every batch has passed independent lab testing. Changed your mind? Email info@sleepstack.nl any time before we ship for a full refund.',
        notify_eyebrow: 'Not ready yet?',
        notify_heading: 'Hear first when subscriptions open.',
        notify_body: 'One email at launch in November/December 2026. No newsletter, no follow-ups.',
        notify_placeholder: 'you@example.com',
        notify_cta: 'Keep me posted',
        notify_sending: 'Sending…',
        notify_success: 'You\'re on the list. See you in November/December.',
        notify_error: 'Something went wrong. Please try again.',
        save_prefix: 'Save',
        per_month: '/month',
        one_time: 'one-off',
    },
    nl: {
        eyebrow: 'Pre-order',
        heading: 'Pre-order Sleepstack.',
        lead: 'Een eenmalige pre-order van €35. Geen abonnement nodig. De drie abonnementen hieronder komen bij de lancering beschikbaar, november/december 2026.',
        bullets: ['Eenmalige pre-order', 'Geen abonnement nodig', 'Verzending na kwaliteitscontrole', 'Refund mogelijk vóór verzending'],
        available_now: 'Nu beschikbaar',
        at_launch: 'Bij lancering · november/december 2026',
        preorder_cta: 'Naar checkout',
        locked_cta: 'Beschikbaar bij lancering',
        fineprint: 'Je wordt vandaag afgeschreven voor €35. We verzenden november/december 2026, pas nadat elke batch onafhankelijk in het lab is getest. Volledige terugbetaling mogelijk tot aan verzending via info@sleepstack.nl.',
        notify_eyebrow: 'Nog niet klaar?',
        notify_heading: 'Krijg bericht zodra abonnementen live gaan.',
        notify_body: 'Eén e-mail bij lancering, november/december 2026. Geen nieuwsbrief, geen vervolgmails.',
        notify_placeholder: 'jij@voorbeeld.com',
        notify_cta: 'Houd me op de hoogte',
        notify_sending: 'Bezig…',
        notify_success: 'Je staat op de lijst. Tot november/december.',
        notify_error: 'Er ging iets mis. Probeer het opnieuw.',
        save_prefix: 'Bespaar',
        per_month: '/maand',
        one_time: 'eenmalig',
    },
};

const TIERS = [
    {
        key: 'preorder',
        active: true,
        priceLabel: '€35',
        unitKey: 'one_time',
        perdose_en: '€1.17 per serving',
        perdose_nl: '€1,17 per dosering',
        title_en: 'Pre-order',
        title_nl: 'Pre-order',
        cadence_en: 'One pouch, shipped in November/December 2026',
        cadence_nl: '1 zakje, eenmalig verzonden november/december 2026',
        specs_en: '30 servings · natural cherry flavour · 7.5 g per serving',
        specs_nl: '30 doseringen · natuurlijke kersensmaak · 7,5 g per dosering',
        extra_en: '€10 under the €45 launch price · charged today',
        extra_nl: '€10 onder de lanceringsprijs van €45 · vandaag afgeschreven',
    },
    {
        key: 'sub1',
        active: false,
        priceLabel: '€42',
        unitKey: 'per_month',
        perdose_en: '€1.40 per serving',
        perdose_nl: '€1,40 per dosering',
        title_en: '1-Month Supply',
        title_nl: '1 maand voorraad',
        cadence_en: '1 pouch (30 servings) delivered every month',
        cadence_nl: '1 zakje (30 doseringen) elke maand geleverd',
        extra_en: 'Billed monthly · skip, pause or cancel any time',
        extra_nl: 'Maandelijks afgeschreven · overslaan, pauzeren of stoppen wanneer je wilt',
        savePct: 7,
    },
    {
        key: 'sub3',
        active: false,
        priceLabel: '€39',
        unitKey: 'per_month',
        perdose_en: '€1.30 per serving',
        perdose_nl: '€1,30 per dosering',
        title_en: '3-Month Supply',
        title_nl: '3 maanden voorraad',
        cadence_en: '3 pouches (90 servings) delivered every 3 months',
        cadence_nl: '3 zakjes (90 doseringen) elke 3 maanden geleverd',
        extra_en: 'Billed every 3 months (€117) · cancel any time',
        extra_nl: 'Elke 3 maanden afgeschreven (€117) · stop wanneer je wilt',
        savePct: 13,
    },
    {
        key: 'sub6',
        active: false,
        priceLabel: '€35',
        unitKey: 'per_month',
        perdose_en: '€1.17 per serving',
        perdose_nl: '€1,17 per dosering',
        title_en: '6-Month Supply',
        title_nl: '6 maanden voorraad',
        cadence_en: '6 pouches (180 servings) delivered every 6 months',
        cadence_nl: '6 zakjes (180 doseringen) elke 6 maanden geleverd',
        extra_en: 'Billed every 6 months (€210) · cancel any time',
        extra_nl: 'Elke 6 maanden afgeschreven (€210) · stop wanneer je wilt',
        savePct: 22,
    },
];

function TierRow({ tier, c, lang, index }) {
    const title = lang === 'nl' ? tier.title_nl : tier.title_en;
    const cadence = lang === 'nl' ? tier.cadence_nl : tier.cadence_en;
    const extra = lang === 'nl' ? tier.extra_nl : tier.extra_en;
    const specs = lang === 'nl' ? tier.specs_nl : tier.specs_en;
    const perdose = lang === 'nl' ? tier.perdose_nl : tier.perdose_en;

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: index * 0.05 }}
            className={
                'relative rounded-xl overflow-visible ' +
                (tier.active
                    ? 'bg-white/60 backdrop-blur-xl border-2 border-primary shadow-sm'
                    : 'bg-white/25 backdrop-blur-xl border border-white/60')
            }
            data-testid={`pricing-tier-${tier.key}`}
            data-active={tier.active ? 'true' : 'false'}
        >
            {/* Top-corner badges */}
            {tier.active && (
                <div
                    className="absolute -top-2.5 left-4 bg-primary text-white text-[10px] font-bold tracking-[0.2em] uppercase px-2.5 py-0.5 rounded"
                    data-testid={`pricing-badge-${tier.key}`}
                >
                    {c.available_now}
                </div>
            )}

            <div
                className={
                    'flex items-center gap-3 sm:gap-4 p-3 sm:p-4 ' +
                    (tier.active ? '' : 'opacity-60 grayscale')
                }
            >
                {/* Product thumbnail */}
                <div className="w-14 h-16 sm:w-16 sm:h-20 shrink-0 flex items-center justify-center">
                    <img
                        src="/product-thumb.webp"
                        alt=""
                        className="max-w-full max-h-full object-contain"
                        loading="lazy"
                        width="240"
                        height="269"
                    />
                </div>

                {/* Middle: title + cadence + extra */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-heading text-base sm:text-lg font-semibold text-foreground leading-tight">
                            {title}
                        </span>
                        {tier.savePct && (
                            <span className="inline-block bg-secondary/15 text-secondary-dark text-[10px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded">
                                {c.save_prefix} {tier.savePct}%
                            </span>
                        )}
                    </div>
                    <div className="text-xs sm:text-[13px] text-muted-foreground mt-0.5 leading-snug">
                        {cadence}
                    </div>
                    {specs && (
                        <div className="text-[11px] sm:text-xs font-semibold text-primary mt-1 leading-snug" data-testid={`pricing-specs-${tier.key}`}>
                            {specs}
                        </div>
                    )}
                    <div className="text-[11px] sm:text-xs text-muted-foreground/80 mt-0.5 leading-snug hidden sm:block">
                        {extra}
                    </div>
                </div>

                {/* Right: price */}
                <div className="text-right shrink-0">
                    {tier.active && (
                        <div className="text-sm text-muted-foreground line-through leading-none mb-1" data-testid={`pricing-strike-${tier.key}`}>
                            €45
                        </div>
                    )}
                    <div className="font-condensed text-3xl sm:text-4xl font-bold text-foreground leading-none">
                        {tier.priceLabel}
                    </div>
                    <div className="text-[11px] sm:text-xs text-muted-foreground mt-0.5">
                        {c[tier.unitKey]}
                    </div>
                    {perdose && (
                        <div className="text-[11px] sm:text-xs text-secondary-dark font-semibold mt-1 whitespace-nowrap" data-testid={`pricing-perdose-${tier.key}`}>
                            {perdose}
                        </div>
                    )}
                </div>

                {/* Far right: CTA */}
                <div className="shrink-0 hidden sm:block">
                    {tier.active ? (
                        <Link
                            to="/checkout"
                            className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white font-semibold rounded-md px-4 py-2.5 text-xs sm:text-sm tracking-wide transition-colors duration-200 whitespace-nowrap"
                            data-testid={`pricing-cta-${tier.key}`}
                        >
                            {c.preorder_cta}
                            <ArrowRight size={14} strokeWidth={2.5} />
                        </Link>
                    ) : (
                        <button
                            type="button"
                            disabled
                            className="inline-flex items-center gap-1.5 bg-slate-200 text-slate-500 font-semibold rounded-md px-4 py-2.5 text-xs sm:text-sm cursor-not-allowed whitespace-nowrap"
                            data-testid={`pricing-cta-${tier.key}`}
                        >
                            <Lock size={12} strokeWidth={2.5} />
                            {c.locked_cta}
                        </button>
                    )}
                </div>
            </div>

            {/* Mobile-only CTA row */}
            <div className="sm:hidden px-3 pb-3">
                {tier.active ? (
                    <Link
                        to="/checkout"
                        className="w-full inline-flex items-center justify-center gap-1.5 bg-primary hover:bg-primary-dark text-white font-semibold rounded-md px-4 py-2.5 text-sm tracking-wide"
                        data-testid={`pricing-cta-mobile-${tier.key}`}
                    >
                        {c.preorder_cta}
                        <ArrowRight size={14} strokeWidth={2.5} />
                    </Link>
                ) : (
                    <button
                        type="button"
                        disabled
                        className="w-full inline-flex items-center justify-center gap-1.5 bg-slate-200 text-slate-500 font-semibold rounded-md px-4 py-2.5 text-sm cursor-not-allowed"
                        data-testid={`pricing-cta-mobile-${tier.key}`}
                    >
                        <Lock size={12} strokeWidth={2.5} />
                        {c.locked_cta}
                    </button>
                )}
            </div>

            {/* "Unlocks at launch" hint for locked tiers */}
            {!tier.active && (
                <div className="absolute -top-2.5 right-4 bg-slate-200 text-slate-500 text-[10px] font-semibold tracking-[0.2em] uppercase px-2.5 py-0.5 rounded inline-flex items-center gap-1">
                    <Lock size={9} strokeWidth={2.5} />
                    <span className="hidden md:inline">{c.at_launch}</span>
                    <span className="md:hidden">{lang === 'nl' ? 'Bij lancering' : 'At launch'}</span>
                </div>
            )}
        </motion.div>
    );
}

function NotifyMeForm({ c, lang }) {
    const [email, setEmail] = useState('');
    const [state, setState] = useState('idle'); // idle | sending | success | error

    async function onSubmit(e) {
        e.preventDefault();
        if (!email || state === 'sending') return;
        setState('sending');
        try {
            const res = await fetch(`${API}/api/notify/subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim(), lang, source: 'pricing_section' }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setState('success');
        } catch (err) {
            setState('error');
        }
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
            className={`mt-6 p-5 sm:p-6 max-w-3xl ${GLASS}`}
            data-testid="notify-me-card"
        >
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1.5">
                {c.notify_eyebrow}
            </div>
            <h3 className="font-heading text-lg sm:text-xl font-semibold text-foreground leading-tight mb-1">
                {c.notify_heading}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                {c.notify_body}
            </p>
            {state === 'success' ? (
                <div
                    className="flex items-center gap-2 text-sm text-primary font-semibold"
                    data-testid="notify-me-success"
                >
                    <Check size={16} strokeWidth={2.5} />
                    {c.notify_success}
                </div>
            ) : (
                <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2" data-testid="notify-me-form">
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={c.notify_placeholder}
                        className="flex-1 rounded-md border border-border bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                        data-testid="notify-me-email-input"
                        disabled={state === 'sending'}
                    />
                    <button
                        type="submit"
                        disabled={state === 'sending'}
                        className="inline-flex items-center justify-center gap-2 bg-foreground hover:bg-primary text-white font-semibold rounded-md px-5 py-2.5 text-sm tracking-wide transition-colors duration-200 disabled:opacity-60 disabled:cursor-wait"
                        data-testid="notify-me-submit"
                    >
                        {state === 'sending' ? (
                            <>
                                <Loader2 size={14} className="animate-spin" strokeWidth={2.5} />
                                {c.notify_sending}
                            </>
                        ) : (
                            <>{c.notify_cta}</>
                        )}
                    </button>
                </form>
            )}
            {state === 'error' && (
                <div className="mt-2 text-xs text-red-600" data-testid="notify-me-error">
                    {c.notify_error}
                </div>
            )}
        </motion.div>
    );
}

export function PricingSection() {
    const { lang } = useLanguage();
    const c = COPY[lang] || COPY.en;

    return (
        <section className="section-padding relative" id="pricing" style={{ scrollMarginTop: '120px' }}>
            <div className="section-container">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className={`relative mt-10 mb-6 md:mb-8 max-w-3xl p-6 sm:p-8 lg:p-10 ${GLASS}`}
                >
                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-primary absolute -top-8 left-1">
                        <span className="w-8 h-px bg-primary" />
                        {c.eyebrow}
                    </div>
                    <h2 className="heading-lg text-foreground mb-4 leading-tight" data-testid="pricing-heading">
                        {c.heading}
                    </h2>
                    <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                        {c.lead}
                    </p>
                    <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2" data-testid="preorder-trust-bullets">
                        {c.bullets.map((b, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-slate-700" data-testid={`preorder-bullet-${i}`}>
                                <Check size={14} className="text-primary shrink-0" strokeWidth={2.5} />
                                {b}
                            </li>
                        ))}
                    </ul>
                </motion.div>

                {/* Compact stacked tier rows */}
                <div className="space-y-3 max-w-3xl" data-testid="pricing-tier-grid">
                    {TIERS.map((tier, i) => (
                        <TierRow key={tier.key} tier={tier} c={c} lang={lang} index={i} />
                    ))}
                </div>

                {/* Fineprint */}
                <p className="mt-6 text-xs text-muted-foreground leading-relaxed max-w-3xl">
                    {c.fineprint}
                </p>

                {/* Notify-me email capture */}
                <NotifyMeForm c={c} lang={lang} />
            </div>
        </section>
    );
}
